import React, { useState } from "react";
import MuiModal from "../atoms/MuiModal";
import InputLabel from "../atoms/InputLabel";
import ButtonComponent from "../atoms/ButtonComponent";
import { Stack, Typography, Alert, CircularProgress } from "@mui/material";
import useUser from "../../hooks/useUser";

const ForgotPasswordModal = ({ open, onClose }) => {
  const { forgotPasswordUser, verifyCodeUser, updatePasswordUser, loader } =
    useUser();
  const [step, setStep] = useState(1); // 1: Identify, 2: Verify, 3: Reset
  const [user, setUser] = useState("");
  const [phoneEnding, setPhoneEnding] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState({ text: "", type: "info" });
  const [idUser, setIdUser] = useState("");

  const handleRequestCode = async () => {
    if (!user) {
      setMessage({ text: "El usuario es obligatorio", type: "error" });
      return;
    }
    try {
      const res = await forgotPasswordUser(user);
      if (res.success === true) {
        // Según la respuesta de la API: { data: { phoneEnding: "4571" } }
        const ending = res.data?.phoneEnding || "";
        setPhoneEnding(ending);

        setMessage({
          text: res.message || "Código enviado al celular registrado",
          type: "success",
        });
        setStep(2);
      } else {
        setMessage({
          text: res.message || "Error al enviar código. Verifique sus datos.",
          type: "error",
        });
      }
    } catch (err) {
      setMessage({ text: err.message, type: "error" });
    }
  };

  const handleVerifyCode = async () => {
    if (!code) {
      setMessage({ text: "El código es obligatorio", type: "error" });
      return;
    }
    try {
      const res = await verifyCodeUser(user, code);
      if (res.success === true) {
        // Capturar idUser de la respuesta: res.data.idUser
        const idFromApi = res.data?.idUser || "";
        setIdUser(idFromApi);

        setMessage({
          text: "Código verificado correctamente",
          type: "success",
        });
        setStep(3);
      } else {
        setMessage({
          text: res.message || "Código inválido o expirado",
          type: "error",
        });
      }
    } catch (err) {
      setMessage({ text: err.message, type: "error" });
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setMessage({ text: "La nueva contraseña es obligatoria", type: "error" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ text: "Las contraseñas no coinciden", type: "error" });
      return;
    }
    try {
      // Enviamos idUser y newPassword según lo solicitado
      const res = await updatePasswordUser({ idUser, password: newPassword });
      if (res.success === true) {
        setMessage({
          text: "Contraseña actualizada con éxito. Redirigiendo...",
          type: "success",
        });
        setTimeout(() => {
          onClose();
          resetForm();
        }, 2500);
      } else {
        setMessage({
          text: res.message || "Error al actualizar la contraseña",
          type: "error",
        });
      }
    } catch (err) {
      setMessage({ text: err.message, type: "error" });
    }
  };

  const resetForm = () => {
    setStep(1);
    setUser("");
    setPhoneEnding("");
    setIdUser("");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setMessage({ text: "", type: "info" });
  };

  return (
    <MuiModal
      title="Recuperar Contraseña"
      visible={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      width="450px"
    >
      <Stack spacing={3} p={3}>
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {step === 1 && (
          <>
            <Typography variant="body2" color="textSecondary">
              Para recuperar tu acceso, ingresa tu nombre de usuario. Te
              enviaremos un código de seguridad al celular registrado.
            </Typography>
            <InputLabel
              label="Usuario"
              value={user}
              setValue={setUser}
              inputType="text"
              inputwidth="100%"
            />
            <Stack direction="row" spacing={2} justifyContent="center">
              <ButtonComponent
                label={
                  loader ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Enviar Código SMS"
                  )
                }
                width="70%"
                onClick={handleRequestCode}
                disabled={loader}
                backgroundColor="#1E88E5"
              />
            </Stack>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="body2" color="textSecondary">
              El código fue enviado al celular terminado en{" "}
              <b>*******{phoneEnding}</b>. Por favor, ingrésalo a continuación
              para continuar.
            </Typography>
            <InputLabel
              label="Código de Verificación"
              value={code}
              setValue={setCode}
              inputType="text"
              inputwidth="100%"
            />

            <Stack direction="row" spacing={2} justifyContent="center">
              <ButtonComponent
                label={
                  loader ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Verificar Código"
                  )
                }
                onClick={handleVerifyCode}
                disabled={loader}
                backgroundColor="#1E88E5"
                width="70%"
              />
              <ButtonComponent
                label="Regresar"
                onClick={() => setStep(1)}
                backgroundColor="#9e9e9e"
                disabled={loader}
                width="70%"
              />
            </Stack>
          </>
        )}

        {step === 3 && (
          <>
            <Typography variant="body2" color="textSecondary">
              Ingresa y confirma tu nueva contraseña de acceso.
            </Typography>
            <InputLabel
              label="Nueva Contraseña"
              value={newPassword}
              setValue={setNewPassword}
              inputType="password"
              inputwidth="100%"
              showEye={true}
            />
            <InputLabel
              label="Confirmar Nueva Contraseña"
              value={confirmPassword}
              setValue={setConfirmPassword}
              inputType="password"
              inputwidth="100%"
              showEye={false}
            />
            <ButtonComponent
              label={
                loader ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Actualizar Contraseña"
                )
              }
              onClick={handleResetPassword}
              disabled={loader}
              backgroundColor="#4CAF50"
              width="70%"
            />
          </>
        )}
      </Stack>
    </MuiModal>
  );
};

export default ForgotPasswordModal;
